<template>
  <v-main
    full-height
    :class="{ 'overflow-y-auto': isOpening, 'bg-gray-50': isOpening && !theme.current.value.dark }"
  >
    <v-container
      :style="isOpening ? undefined : 'height: 100%'"
      class="flex flex-col"
      :fluid="!isOpening"
      :full-height="!isOpening"
    >
      <div
        class="flex flex-col px-1"
        :style="isOpening ? undefined : 'flex: 1 1 0; overflow: auto; position: relative'"
      >
        <router-view v-slot="{ Component }">
          <keep-alive>
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
      <PendingTransferRequestDetailDrawer :account-id="props.accountId" />
      <TransactionDetailDrawer :account-id="props.accountId" />
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import { useAccountTabs } from "@/composables/useAccountTabs";
import { useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import { useUserStore } from "@/store/user";
import type { Account } from "@aircap/common/account";
import { collection, doc } from "firebase/firestore";
import { computed, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTheme } from "vuetify";
import PendingTransferRequestDetailDrawer from "./components/PendingTransferRequestDetailDrawer.vue";
import TransactionDetailDrawer from "./components/TransactionDetailDrawer.vue";

const props = defineProps<{
  accountId: string;
}>();
const user = useUserStore();
const route = useRoute();
const router = useRouter();
const theme = useTheme();
const isOpening = computed(() => route.name === "Opening");

const accountSource = computed(() => doc(collection<Account>(db, "accounts"), props.accountId));
const { data: account, pending: accountLoading } = useDocument<Account>(accountSource);

watch(
  account,
  (account) => {
    document.title = [account?.name, "Aircap"].filter((x) => x).join(" - ");
  },
  { immediate: true },
);

const { treezorKycReadyForAdminReview, treezorOnboarding } = useAccountTabs(
  computed(() => account.value ?? undefined),
);

// Redirect the bare /account/:accountId route to its home tab. Done here rather than in a route
// guard because `beforeEnter` does not run when only params change (e.g. switching accounts).
watchEffect(() => {
  if (route.name !== "Account" || accountLoading.value) return;
  // `account` still holds the previous document until the new accountId snapshot arrives
  if (account.value && account.value.id !== props.accountId) return;
  let name = "Dashboard";
  if (treezorOnboarding.value && (user.isKind("admin") || user.isKind("user"))) name = "Opening";
  if (treezorKycReadyForAdminReview.value && user.isKind("admin")) name = "Opening";
  void router.replace({ name, params: { accountId: props.accountId }, query: route.query });
});
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 0px;
  z-index: 2;
}
</style>
